import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Workflow, Users, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnrollCampaignModal({ isOpen, onClose, contacts = [], onSuccess }) {
    const { getAuthHeader } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState('');
    const [loading, setLoading] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadCampaigns();
            setResult(null);
            setSelectedCampaign('');
        }
    }, [isOpen]);

    const loadCampaigns = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/campaigns`, { headers: getAuthHeader() });
            setCampaigns(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch (err) {
            toast.error('Failed to load campaigns');
        } finally {
            setLoading(false);
        }
    };

    const handleEnroll = async () => {
        if (!selectedCampaign) {
            toast.error('Please select a campaign');
            return;
        }

        setEnrolling(true);
        try {
            const contactIds = contacts.map(c => c.contact_id);
            const res = await axios.post(
                `${API}/campaigns/${selectedCampaign}/bulk-enroll`,
                { contact_ids: contactIds },
                { headers: getAuthHeader() }
            );

            setResult(res.data);

            if (res.data.enrolled > 0) {
                toast.success(`Enrolled ${res.data.enrolled} contact(s) in campaign`);
                if (onSuccess) onSuccess(res.data);
            } else if (res.data.skipped > 0) {
                toast.info('All contacts were already enrolled or not found');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to enroll contacts');
        } finally {
            setEnrolling(false);
        }
    };

    if (!isOpen) return null;

    const selectedCamp = campaigns.find(c => c.campaign_id === selectedCampaign);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden animate-fade-in">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                            <Workflow className="w-5 h-5 text-stone-700" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Enroll in Campaign
                            </h2>
                            <p className="text-sm text-stone-500">
                                {contacts.length} contact{contacts.length !== 1 ? 's' : ''} selected
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" data-testid="close-enroll-modal">
                        <X className="w-5 h-5 text-stone-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-5">
                    {/* Selected Contacts Preview */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-stone-700 mb-2">Selected Contacts</label>
                        <div className="bg-stone-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                            {contacts.slice(0, 5).map((contact, idx) => (
                                <div key={contact.contact_id || idx} className="flex items-center gap-2 py-1">
                                    <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-xs font-medium text-stone-700">
                                        {(contact.first_name?.[0] || contact.email?.[0] || '?').toUpperCase()}
                                    </div>
                                    <span className="text-sm text-stone-700">
                                        {contact.first_name && contact.last_name
                                            ? `${contact.first_name} ${contact.last_name}`
                                            : contact.email || 'Unknown'}
                                    </span>
                                    {contact.title && <span className="text-xs text-stone-400">• {contact.title}</span>}
                                </div>
                            ))}
                            {contacts.length > 5 && (
                                <p className="text-xs text-stone-500 mt-1">+ {contacts.length - 5} more contacts</p>
                            )}
                        </div>
                    </div>

                    {/* Campaign Selection */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-stone-700 mb-2">Select Campaign</label>
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 text-stone-700 animate-spin" />
                            </div>
                        ) : campaigns.length === 0 ? (
                            <div className="text-center py-8 bg-stone-50 rounded-lg">
                                <Workflow className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                                <p className="text-sm text-stone-500">No campaigns available</p>
                                <p className="text-xs text-stone-400 mt-1">Create a campaign first</p>
                            </div>
                        ) : (
                            <select
                                value={selectedCampaign}
                                onChange={(e) => setSelectedCampaign(e.target.value)}
                                className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500"
                                data-testid="campaign-select"
                            >
                                <option value="">Choose a campaign...</option>
                                {campaigns.map(camp => (
                                    <option key={camp.campaign_id} value={camp.campaign_id}>
                                        {camp.name} ({camp.type || 'journey'}) - {camp.status}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Selected Campaign Info */}
                    {selectedCamp && (
                        <div className="bg-stone-100 rounded-lg p-3 mb-5">
                            <div className="flex items-center gap-2 mb-1">
                                <Workflow className="w-4 h-4 text-stone-700" />
                                <span className="text-sm font-medium text-stone-900">{selectedCamp.name}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    selectedCamp.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-100 text-stone-600'
                                }`}>
                                    {selectedCamp.status}
                                </span>
                            </div>
                            <p className="text-xs text-stone-900">
                                Type: {selectedCamp.type || 'journey'} • {selectedCamp.total_enrolled || 0} enrolled
                            </p>
                        </div>
                    )}

                    {/* Result Display */}
                    {result && (
                        <div className={`rounded-lg p-4 mb-5 ${result.enrolled > 0 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                            <div className="flex items-start gap-3">
                                {result.enrolled > 0 ? (
                                    <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
                                ) : (
                                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                                )}
                                <div>
                                    <p className={`text-sm font-medium ${result.enrolled > 0 ? 'text-emerald-900' : 'text-amber-900'}`}>
                                        {result.enrolled > 0
                                            ? `Successfully enrolled ${result.enrolled} contact(s)`
                                            : 'No new contacts enrolled'}
                                    </p>
                                    {result.skipped > 0 && (
                                        <p className="text-xs text-stone-600 mt-1">
                                            {result.skipped} contact(s) skipped (already enrolled or not found)
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
                    <Button variant="outline" onClick={onClose} data-testid="cancel-enroll-btn">
                        {result ? 'Close' : 'Cancel'}
                    </Button>
                    {!result && (
                        <Button
                            onClick={handleEnroll}
                            disabled={!selectedCampaign || enrolling}
                            className="bg-stone-900 hover:bg-stone-800"
                            data-testid="confirm-enroll-btn"
                        >
                            {enrolling ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Enrolling...
                                </>
                            ) : (
                                <>
                                    <Users className="w-4 h-4 mr-2" />
                                    Enroll {contacts.length} Contact{contacts.length !== 1 ? 's' : ''}
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
